/**
 * InviteBannerContainer (Phase 2 of the shareable join link, #171).
 *
 * Reads the `?invite=<token>` query param, resolves it against the backend, and
 * — for a valid token — renders the floating InviteBanner with the real
 * ecosystem + inviter and wires the CTAs:
 *
 *   - Explore            -> the ecosystem's /directory (banner persists)
 *   - Primary CTA:
 *       logged out       -> "Sign up"  -> /signup; the ecosystem is stashed via
 *                            pendingInvite (localStorage) so the join completes
 *                            after authentication (not via the URL)
 *       logged in, not a member -> "Join" -> joins the ecosystem, then /dashboard
 *       logged in, already a member -> "Go to dashboard" -> /dashboard
 *   - X (dismiss)        -> hides the banner for the current visit; a reload or
 *                            reopening the link brings it back
 *
 * Persistence: the token is mirrored into sessionStorage so the banner survives
 * client-side navigation that drops the query param (e.g. clicking Explore), and
 * clears once the recipient joins or the token proves invalid. Dismissal is
 * deliberately in-memory only (not persisted) so a reload restores the invite.
 * The banner is hidden on the auth pages (signup/login) where it would just
 * clutter the form.
 */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { useInviteApi } from '@/shared/services/inviteApi';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';
import { getEcosystemDestinationUrl } from '@/shared/utils/ecosystemRouting';

import { setPendingInvite } from '../pendingInvite';

import InviteBanner from './InviteBanner';

// sessionStorage key for the pending invite (dismissal is in-memory only).
const TOKEN_KEY = 'inviteToken';

const readSession = (key) => {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
};
const writeSession = (key, value) => {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        /* private mode / storage disabled — non-fatal */
    }
};
const clearSession = (key) => {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        /* non-fatal */
    }
};

export default function InviteBannerContainer({
    onVisibilityChange = () => {},
}) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { user } = useAuth();
    const { isMember, loading: membershipLoading, refresh } = useMembership();
    const inviteApi = useInviteApi();
    const ecosystemApi = useEcosystemApi();

    // Token comes from the URL first, then the persisted copy so the banner
    // survives navigation that drops the query param.
    const urlToken = searchParams.get('invite');
    const token = urlToken || readSession(TOKEN_KEY);

    const [invite, setInvite] = useState(null);
    const [joining, setJoining] = useState(false);
    // In-memory only: a reload restores the invite rather than trapping it hidden.
    const [dismissed, setDismissed] = useState(false);

    // Persist a token that arrived via the URL.
    useEffect(() => {
        if (urlToken) {
            writeSession(TOKEN_KEY, urlToken);
        }
    }, [urlToken]);

    const resolve = useCallback(async () => {
        if (!token) {
            setInvite(null);
            return;
        }
        try {
            const data = await inviteApi.resolveInvite(token);
            setInvite(data);
            // Stop re-showing a bad token on every navigation.
            if (!data || !data.valid) {
                clearSession(TOKEN_KEY);
            }
        } catch {
            setInvite({ valid: false });
        }
    }, [inviteApi, token]);

    useEffect(() => {
        resolve();
    }, [resolve]);

    // Clear the pending invite and go into the INVITED ecosystem's dashboard.
    // Full reload (window.location) to the invited ecosystem — not whatever
    // tenant the page happens to be on — so the destination always matches the
    // join, and EcosystemProvider re-bootstraps under that tenant (mirrors
    // EcosystemChooser).
    const finish = useCallback(() => {
        clearSession(TOKEN_KEY);
        window.location.href =
            getEcosystemDestinationUrl(invite.ecosystem) + '/dashboard';
    }, [invite]);

    const handleExplore = useCallback(() => {
        // Leave the token persisted so the banner stays while they browse.
        navigate('/directory');
    }, [navigate]);

    const handlePrimary = useCallback(async () => {
        // Logged out: stash the ecosystem so the join completes after they
        // authenticate (EcosystemChooser reads this), then hand off to signup.
        if (!user) {
            if (invite?.ecosystem?.slug) {
                setPendingInvite(invite.ecosystem.slug);
            }
            // Clear the session token so the banner doesn't re-appear on the
            // dashboard after the post-auth auto-join + reload; the pending
            // invite (localStorage) is what completes the join, not the URL.
            clearSession(TOKEN_KEY);
            navigate('/signup');
            return;
        }
        // Already a member: nothing to join, just go inside.
        if (isMember) {
            finish();
            return;
        }
        // Logged-in non-member: join, then route into the ecosystem.
        try {
            setJoining(true);
            await ecosystemApi.joinEcosystem(invite.ecosystem.slug);
            await refresh();
            finish();
        } catch (err) {
            console.error('Failed to join from invite:', err);
            setJoining(false);
        }
    }, [user, isMember, invite, ecosystemApi, refresh, navigate, finish]);

    const handleDismiss = useCallback(() => {
        setDismissed(true);
    }, []);

    // Hide on the auth pages — the floating bar would clutter the form there.
    const onAuthPage =
        location.pathname.startsWith('/signup') ||
        location.pathname.startsWith('/login');

    const visible = Boolean(
        token &&
        invite &&
        invite.valid &&
        invite.ecosystem &&
        !dismissed &&
        !onAuthPage
    );

    // An invite token was present in the URL but did not resolve (expired,
    // invalid, or unknown). Show a clear notice — but ONLY when a token was
    // actually attempted, never on a plain visit with no token (#171, AC #5).
    const invalidVisible = Boolean(
        token && invite && invite.valid === false && !dismissed && !onAuthPage
    );

    const floating = visible || invalidVisible;

    // Report visibility up so the default JoinEcosystemBanner suppresses from
    // the same source of truth (no double banner, #171). Also tag <body> while
    // floating so the chat widget lifts above the bar (see InviteBanner.css).
    useEffect(() => {
        onVisibilityChange(floating);
        if (!floating) return undefined;
        document.body.classList.add('has-invite-banner');
        return () => document.body.classList.remove('has-invite-banner');
    }, [floating, onVisibilityChange]);

    if (visible) {
        let primaryLabel;
        if (!user) {
            primaryLabel = 'Sign up';
        } else if (isMember) {
            primaryLabel = 'Go to dashboard';
        } else if (joining) {
            primaryLabel = 'Joining…';
        } else {
            primaryLabel = 'Join';
        }

        return (
            <InviteBanner
                ecosystemName={invite.ecosystem.name}
                inviterName={invite.inviter?.name}
                primaryLabel={primaryLabel}
                onPrimary={handlePrimary}
                onExplore={handleExplore}
                onDismiss={handleDismiss}
                primaryDisabled={
                    joining || (Boolean(user) && membershipLoading)
                }
            />
        );
    }

    if (invalidVisible) {
        return (
            <div className="invite-notice" role="alert">
                <span>This invite link is no longer valid or has expired.</span>
                <button
                    type="button"
                    className="invite-banner__close"
                    aria-label="Dismiss notice"
                    onClick={handleDismiss}
                >
                    <svg
                        viewBox="0 0 16 16"
                        width="11"
                        height="11"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <path d="M3 3 L13 13 M13 3 L3 13" />
                    </svg>
                </button>
            </div>
        );
    }

    return null;
}

InviteBannerContainer.propTypes = {
    // Called with a boolean whenever the banner/notice shows or hides, so the
    // parent can suppress the default join prompt (single source of truth).
    onVisibilityChange: PropTypes.func,
};
